import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import type { AxiosError } from 'axios';
import {
  useConfirmMissingRows,
  useSupplierPriceListRuns,
} from '@/api/supplier-price-lists/supplier-price-lists.hooks';
import type { RunReport, RunStatus } from '@/api/supplier-price-lists/supplier-price-lists.types';
import { PERMISSIONS } from '@/constants/permissions';
import { DsAlert, DsButton, DsModal } from '@/modules/internal-v2/ds';
import { DsTable } from '../../shared/ds-table.component';
import { ContainedPage } from '../../shared/contained-page.component';
import { IfPermitted } from '../../shared/if-permitted.component';
import {
  fmtCount,
  fmtDateTime,
  missingOffers,
  missingOffersText,
  offersText,
} from './supplier-feed-missing-offers';

const STATUS_LABELS: Record<RunStatus, string> = {
  pending: 'Warten',
  fetching: 'Abruf',
  fetched: 'Abgerufen',
  processing: 'Verarbeitung',
  success: 'Erfolg',
  partial: 'Teilweise',
  failed: 'Fehler',
  skipped: 'Übersprungen',
};

const STATUS_PILL: Record<RunStatus, string> = {
  pending: 'muted',
  fetching: 'muted',
  fetched: 'muted',
  processing: 'muted',
  success: 'pos',
  partial: 'warn',
  failed: 'neg',
  skipped: 'muted',
};

function formatDuration(start: string, end: string | null): string {
  if (!end) return '…';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const col = createColumnHelper<RunReport>();

const columns = [
  col.accessor('status', {
    header: 'Status',
    size: 100,
    cell: (info) => {
      const s = info.getValue();
      return <span className={`ds-pill ${STATUS_PILL[s]}`}>{STATUS_LABELS[s]}</span>;
    },
  }),
  col.accessor('startedAt', {
    header: 'Gestartet',
    size: 130,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
        {fmtDate(info.getValue())}
      </span>
    ),
  }),
  col.display({
    id: 'duration',
    header: 'Dauer',
    size: 80,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
        {formatDuration(info.row.original.startedAt, info.row.original.finishedAt)}
      </span>
    ),
  }),
  col.accessor('rowsTotal', {
    header: 'Gesamt',
    size: 65,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('rowsImported', {
    header: 'Import.',
    size: 65,
    meta: { numeric: true },
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>
    ),
  }),
  col.accessor('rowsFailed', {
    header: 'Fehler',
    size: 65,
    meta: { numeric: true },
    cell: (info) => {
      const v = info.getValue();
      if (!v) return <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-4)' }}>0</span>;
      return (
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--neg)', fontWeight: 600, cursor: 'pointer' }}>
          {v}
        </span>
      );
    },
  }),
  col.accessor('sourceHash', {
    header: 'Hash',
    size: 100,
    cell: (info) => {
      const v = info.getValue();
      return v ? (
        <span
          style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}
          title={v}
        >
          {v.slice(0, 12)}…
        </span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
    enableSorting: false,
  }),
];

/**
 * What happened to the offers a run did not contain (EOS-184): the warning of
 * an incomplete-looking run with the button to hide them, or who hid them.
 */
function MissingOffersCell({
  run,
  canConfirm,
  onConfirm,
}: {
  run: RunReport;
  /** The run is the newest processed run of the feed and waits for a decision. */
  canConfirm: boolean;
  onConfirm: (run: RunReport) => void;
}) {
  if (run.missingConfirmedAt) {
    const text = `${offersText(run.rowsDeactivated ?? 0)} ausgeblendet am ${fmtDateTime(run.missingConfirmedAt)} von ${run.missingConfirmedByEmail ?? 'unbekannt'}`;
    return (
      <span
        className="spl-run-check-text"
        style={{ color: 'var(--ink-2)' }}
        title={`${text}. Der Lauf hatte ${fmtCount(run.rowsSeen ?? 0)} von ${fmtCount(run.rowsActiveBefore ?? 0)} Angeboten gesehen.`}
      >
        {text}
      </span>
    );
  }
  if (run.deactivationCheck === 'skipped_incomplete') {
    const text = `Unvollständig? ${fmtCount(run.rowsSeen ?? 0)} von ${fmtCount(run.rowsActiveBefore ?? 0)} Angeboten gesehen – nichts ausgeblendet`;
    return (
      <span className="spl-run-check">
        <span className="ds-flag warn spl-run-check-text" title={text}>
          {text}
        </span>
        {canConfirm && (
          <IfPermitted permission={PERMISSIONS.SUPPLIER_PRICE_LISTS_MANAGE}>
            <DsButton
              size="small"
              onClick={(e) => {
                // The row itself opens the error log.
                e.stopPropagation();
                onConfirm(run);
              }}
            >
              Fehlende Angebote ausblenden
            </DsButton>
          </IfPermitted>
        )}
      </span>
    );
  }
  if (run.deactivationCheck === 'deactivated' && run.rowsDeactivated) {
    return (
      <span className="spl-run-check-text" style={{ color: 'var(--ink-3)' }}>
        {offersText(run.rowsDeactivated)} nicht mehr im Feed, ausgeblendet
      </span>
    );
  }
  return null;
}

interface Props {
  feedId: string;
  feedName: string;
  /** The run whose missing offers wait for a decision (`incompleteRun` of the feed). */
  incompleteRunId: string | null;
}

export function SupplierFeedRuns({ feedId, feedName, incompleteRunId }: Props) {
  const { data, isPending } = useSupplierPriceListRuns(feedId, { limit: 50 });
  const [selectedRun, setSelectedRun] = useState<RunReport | null>(null);
  // The incomplete run whose missing offers wait for the confirmation.
  const [confirming, setConfirming] = useState<RunReport | null>(null);

  const runs = data?.data ?? [];

  const allColumns = useMemo(
    () => [
      ...columns,
      col.display({
        id: 'missingOffers',
        header: 'Fehlende Angebote',
        meta: { flex: true },
        cell: (info) => (
          <MissingOffersCell
            run={info.row.original}
            canConfirm={info.row.original.id === incompleteRunId}
            onConfirm={setConfirming}
          />
        ),
      }),
    ],
    [incompleteRunId],
  );

  const table = useReactTable({
    data: runs,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <ContainedPage contained>
      <DsTable
        table={table}
        fixedLayout
        onRowClick={(run) => {
          if (run.rowsFailed > 0 && run.errorLog?.length) setSelectedRun(run);
        }}
        emptyMessage={isPending ? 'Laden…' : 'Noch keine Läufe.'}
      />

      {selectedRun && (
        <RunErrorOverlay run={selectedRun} onClose={() => setSelectedRun(null)} />
      )}

      {confirming && (
        <ConfirmMissingDialog
          feedId={feedId}
          feedName={feedName}
          run={confirming}
          onClose={() => setConfirming(null)}
        />
      )}
    </ContainedPage>
  );
}

function confirmErrorMessage(error: AxiosError): string {
  const status = error.response?.status;
  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  // 404 and 409 carry an explaining German message from the backend.
  if ((status === 404 || status === 409) && typeof message === 'string') return message;
  if (status === 403) return 'Ihnen fehlt das Recht „Lieferantenpreislisten verwalten“.';
  return 'Die Angebote konnten nicht ausgeblendet werden. Bitte versuchen Sie es erneut.';
}

function ConfirmMissingDialog({
  feedId,
  feedName,
  run,
  onClose,
}: {
  feedId: string;
  feedName: string;
  run: RunReport;
  onClose: () => void;
}) {
  const { mutate, isPending, error } = useConfirmMissingRows(feedId);
  const missing = missingOffers(run);

  return (
    <DsModal
      open
      onClose={onClose}
      title="Fehlende Angebote ausblenden"
      description={`${feedName} · Lauf vom ${fmtDateTime(run.startedAt)}`}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={isPending}>
            Abbrechen
          </DsButton>
          <DsButton
            variant="primary"
            onClick={() => mutate(run.id, { onSuccess: onClose })}
            disabled={isPending}
          >
            {isPending ? 'Ausblenden…' : `${offersText(missing)} ausblenden`}
          </DsButton>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
        Dieser Lauf hat nur {fmtCount(run.rowsSeen ?? 0)} von{' '}
        {fmtCount(run.rowsActiveBefore ?? 0)} aktiven Angeboten gesehen.{' '}
        <strong>{missingOffersText(missing)}</strong>{' '}
        {missing === 1 ? 'verschwindet' : 'verschwinden'} dann aus dem Preis-Tool und{' '}
        {missing === 1 ? 'zählt' : 'zählen'} nicht mehr beim besten Angebot.
      </p>
      <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
        Nur bestätigen, wenn der Lieferant sein Sortiment wirklich verkleinert hat. Taucht ein
        Angebot in einem späteren Lauf wieder auf, wird es automatisch wieder eingeblendet.
      </p>
      {error && (
        <DsAlert severity="error" style={{ marginTop: 8 }}>
          {confirmErrorMessage(error)}
        </DsAlert>
      )}
    </DsModal>
  );
}

function RunErrorOverlay({ run, onClose }: { run: RunReport; onClose: () => void }) {
  const errorTable = useReactTable({
    data: run.errorLog ?? [],
    columns: [
      createColumnHelper<{ row: number; reason: string }>().accessor('row', {
        header: 'Zeile',
        size: 70,
        meta: { numeric: true },
        cell: (info) => <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>,
      }),
      createColumnHelper<{ row: number; reason: string }>().accessor('reason', {
        header: 'Grund',
        meta: { flex: true },
        cell: (info) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{info.getValue()}</span>,
      }),
    ],
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_, i) => String(i),
  });

  return (
    <div className="bp-overlay">
      <div className="bp-overlay-bar">
        <button className="bp-cmd-back" onClick={onClose}>← Zurück</button>
        <span className="bp-overlay-title">Fehler-Log</span>
        <span className="ds-pill neg">{run.errorLog?.length ?? 0} Fehler</span>
      </div>
      <div style={{ padding: '8px 14px', display: 'flex', gap: 6, borderBottom: '1px solid var(--border)' }}>
        <span className={`ds-pill ${STATUS_PILL[run.status]}`}>{STATUS_LABELS[run.status]}</span>
        <span className="ds-pill muted">{fmtDate(run.startedAt)}</span>
      </div>
      <DsTable table={errorTable} fixedLayout emptyMessage="Keine Fehler." />
    </div>
  );
}
